import React, { FC, useState } from 'react';
import styled from 'styled-components';
import { mobile, tablet } from 'styles/breakpoints';
import Text from 'components/Text';
import ContinueButton from './QuizContinueButton';
import quizSvgs from 'utils/constants/quizSvgs';
import GooglePlacesAutocomplete, {
  geocodeByPlaceId,
} from 'react-google-places-autocomplete';
import { config } from 'config';
import { handleError } from 'utils/error';
import InputX from 'assets/icons/yellow-x.svg';
import { useSelector } from 'react-redux';
import { AppState } from 'state/types';

interface InfoProps {
  onContinue: (answer: AnswerProps) => void;
  title?: string;
  subtitle?: string;
  buttonTitle?: string;
  backgroundSvg?: string;
}

interface AnswerProps {
  lat: number;
  lng: number;
  place_id: string;
  main_text: string;
  secondary_text: string;
  full_text: string;
  input_text: string;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 3.5rem 1rem;
  max-width: 28.75rem;
  width: 100%;
  @media ${tablet} {
    padding-top: 1.5rem;
  }
`;

const Title = styled(Text)`
  color: #000;
  font-size: 1.75rem;
  font-weight: 600;
  line-height: 2.0625rem;
  margin-bottom: 0.5rem;
  text-align: center;
  @media ${tablet} {
    font-size: 1.375rem;
    line-height: 2rem;
    font-weight: 700;
  }
`;

const Subtitle = styled(Text)`
  color: #000;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5rem;
  text-align: center;
  margin-bottom: 1.5rem;
  @media ${tablet} {
    font-size: 0.8125rem;
    line-height: normal;
    margin-bottom: 1rem;
  }
`;

const Content = styled.div`
  width: 100%;
  max-width: 28.75rem;
  display: flex;
  flex-direction: column;
`;

const BackgroundSvg = styled.div`
  display: flex;
  justify-content: center;
  overflow: hidden;
  position: absolute;
  bottom: 0;
  width: 100%;
  max-width: 48.125rem;
  z-index: -1;
  @media ${tablet} {
    max-width: 28.1875rem;
    max-height: 21.125rem;
    svg {
      min-width: 28.1875rem;
    }
  }
`;

const StyledBox = styled.div`
  width: 100%;
  position: relative;
`;

const LabelContainer = styled.div`
  display: flex;
  position: absolute;
  pointer-events: none;
  z-index: 2;
  height: 4rem;
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  font-size: 1rem;
  pointer-events: none;
  color: #4d4d4d;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  padding: 0.75rem 1rem;
  height: 4rem;
  :before {
    content: 'Type in your place of birth';
  }
`;

const SelectedLabel = styled.p<{ isVisible: boolean }>`
  display: ${({ isVisible }) => (isVisible ? 'flex' : 'none')};
  align-items: center;
  font-size: 1rem;
  pointer-events: none;
  color: #000;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  padding: 0.75rem 1rem;
  height: 4rem;
  margin-left: 3px;
`;

const StyledX = styled(InputX)`
  position: absolute;
  right: 1rem;
  cursor: pointer;
  z-index: 3;
  top: 1.2rem;
`;

const PlaceInput: FC<InfoProps> = ({
  title,
  subtitle,
  buttonTitle,
  backgroundSvg,
  onContinue,
  ...props
}) => {
  const quiz_answers = useSelector(
    (state: AppState) => state.user.quiz_answers,
  );
  const [value, setValue] = useState(
    quiz_answers?.birth_place?.input_text || '',
  );
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [activeInput, setActiveInput] = useState(false);
  const [loading, setLoading] = useState(false);
  const [coordinates, setCoordinates] = useState<{
    lat: number;
    lng: number;
  } | null>(null);

  const renderSvgImage = (svgKey: string) => {
    const Svg = quizSvgs[svgKey];
    return (
      <BackgroundSvg>
        <Svg />
      </BackgroundSvg>
    );
  };

  const handleInputChange = (
    inputValue: string,
    { action }: { action: string },
  ) => {
    if (action === 'input-change') {
      setValue(inputValue);
      setSelectedPlace(null);
    }
  };

  const retryGeocodeByPlaceId = async (placeId, retries = 3) => {
    for (let i = 0; i < retries; i++) {
      try {
        const results = await geocodeByPlaceId(placeId);
        if (results) {
          return results;
        }
      } catch (error) {
        console.error(`Attempt ${i + 1} failed: ${error.message}`);
      }
    }
    throw new Error('Failed to fetch geocode data after 3 retries');
  };

  const handleSelect = async place => {
    setActiveInput(false);
    setSelectedPlace(place);
    setValue(place.label);
    try {
      setLoading(true);
      const results = await retryGeocodeByPlaceId(place.value.place_id);
      const location = results[0].geometry.location;
      const coords = { lat: location.lat(), lng: location.lng() };
      setCoordinates(coords);
      setLoading(false);
    } catch (error) {
      handleError(error);
      setLoading(false);
    }
  };

  const handleContinue = () => {
    onContinue({
      lat: coordinates?.lat || quiz_answers?.birth_place?.lat,
      lng: coordinates?.lng || quiz_answers?.birth_place?.lng,
      place_id:
        selectedPlace?.value.place_id || quiz_answers?.birth_place?.place_id,
      main_text:
        selectedPlace?.value.terms[0].value ||
        quiz_answers?.birth_place?.main_text,
      secondary_text:
        selectedPlace?.value.terms[selectedPlace.value.terms.length - 1]
          .value || quiz_answers?.birth_place?.secondary_text,
      full_text: selectedPlace?.value.terms[0].value
        ? `${selectedPlace?.value.terms[0].value}, ${
            selectedPlace?.value.terms[selectedPlace.value.terms.length - 1]
              .value
          }`
        : `${quiz_answers?.birth_place?.main_text}, ${quiz_answers?.birth_place?.secondary_text}`,
      input_text: selectedPlace?.label || quiz_answers?.birth_place?.input_text,
    });
  };

  const customStyles = {
    input: provided => ({
      ...provided,
      outline: 'none',
      boxShadow: 'none',
      color: '#000',
    }),
    control: provided => ({
      ...provided,
      outline: 'none',
      boxShadow: 'none',
      padding: '0.75rem 1rem',
      height: '4rem',
      border: '1px solid #AFAFAF',
      borderRadius: '0.75rem',
      color: '#000',
      fontSize: '1rem',
      width: '100%',
      '&:hover': {},
    }),
    valueContainer: provided => ({
      ...provided,
      padding: '0',
    }),
    loadingIndicator: provided => ({
      ...provided,
      position: 'absolute', // Make sure it's absolutely positioned
      right: '2rem', // Adjust the position as needed
    }),
  };

  return (
    <Container {...props}>
      <Content>
        <Title>{title}</Title>
        <Subtitle>{subtitle}</Subtitle>
        <StyledBox>
          <LabelContainer>
            {!value && <Label />}
            {selectedPlace && !activeInput && (
              <SelectedLabel isVisible={!activeInput}>
                {selectedPlace.label}
              </SelectedLabel>
            )}
          </LabelContainer>
          {!!value.length && (
            <StyledX
              onClick={() => {
                setValue('');
                setSelectedPlace(null);
              }}
            />
          )}
          <GooglePlacesAutocomplete
            apiKey={config.GOOGLE_LOCATION_KEY}
            apiOptions={{ language: 'en' }}
            autocompletionRequest={{
              types: ['(cities)'],
            }}
            debounce={500}
            selectProps={{
              value: selectedPlace
                ? { value: selectedPlace.label, label: selectedPlace.label }
                : { value, label: value },
              inputValue: value,
              onFocus: () => setActiveInput(true),
              onInputChange: handleInputChange,
              onChange: handleSelect,
              placeholder: '',
              components: {
                DropdownIndicator: () => null,
                IndicatorSeparator: () => null,
              },
              styles: customStyles,
            }}
          />
        </StyledBox>
        <ContinueButton
          title={buttonTitle}
          onClick={handleContinue}
          loading={loading}
          disabled={
            loading || (quiz_answers?.birth_place ? !value : !selectedPlace)
          }
        />
      </Content>
      {renderSvgImage(backgroundSvg)}
    </Container>
  );
};

export default PlaceInput;
